import {
  LISTEN_THRESHOLD_MS_DEFAULT,
  LIVE_TICK_MS,
  PLAYBACK_POLL_INTERVAL_MS,
  SEARCH_IDLE_RESET_MS,
  formatTimeOnSet,
} from '@mixwire/contracts';
import { useState, type ReactElement } from 'react';

import { loginReturningTo } from '../api/auth.js';
import { readApiFailure, type ApiFailure } from '../api/client.js';
import { AccentCard } from '../components/AccentCard.js';
import { Avatar } from '../components/Avatar.js';
import { EndPartyConfirmModal } from '../components/EndPartyConfirmModal.js';
import { ErrorBanner } from '../components/ErrorBanner.js';
import { InfoTooltip } from '../components/InfoTooltip.js';
import { LeaveModal } from '../components/LeaveModal.js';
import { LoadingStatus } from '../components/LoadingStatus.js';
import { Logo } from '../components/Logo.js';
import { NowPlayingBlock } from '../components/NowPlayingBlock.js';
import { PartyEndedModal } from '../components/PartyEndedModal.js';
import { pillButton } from '../components/pillButton.js';
import {
  PremiumRequiredBanner,
  PremiumRequiredWriteNotice,
} from '../components/PremiumRequiredNotice.js';
import { RateLimitBanner, RateLimitLine } from '../components/RateLimitNotice.js';
import { ShareModal } from '../components/ShareModal.js';
import { SyncMeter } from '../components/SyncMeter.js';
import { TrackFlagNote } from '../components/TrackFlagNote.js';
import { TrackRow } from '../components/TrackRow.js';
import { TrackSearch } from '../components/TrackSearch.js';
import { TrackTile } from '../components/TrackTile.js';
import { TransportButton } from '../components/TransportButton.js';
import { useAuthStatus } from '../hooks/useAuth.js';
import { useDebouncedValue } from '../hooks/useDebouncedValue.js';
import { useDismissWant } from '../hooks/useDismissWant.js';
import { useDjSearch } from '../hooks/useDjSearch.js';
import { useDjView } from '../hooks/useDjView.js';
import { useEndParty } from '../hooks/useEndParty.js';
import { useIdleReset } from '../hooks/useIdleReset.js';
import { useLeaveChannel } from '../hooks/useLeaveChannel.js';
import { useLiveProgress } from '../hooks/useLiveProgress.js';
import { useNow } from '../hooks/useNow.js';
import { useNowPlaying } from '../hooks/useNowPlaying.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useQueueTrack } from '../hooks/useQueueTrack.js';
import { useQueueWant } from '../hooks/useQueueWant.js';
import { useSkipCurrent } from '../hooks/useSkipCurrent.js';
import { useTransport } from '../hooks/useTransport.js';
import { colourName } from '../lib/channelColour.js';
import { withoutNowPlaying } from '../lib/playedHistory.js';
import { formatStartsIn } from '../lib/time.js';
import { LOBBY_PATH, lobbyJoinPath, partyPath } from '../routing/paths.js';
import { useRoute } from '../routing/useRoute.js';

import type {
  Channel,
  ChannelId,
  SessionUser,
  CrowdWant,
  ChannelNowPlaying,
  NowPlaying,
  OtherChannel,
  DjQueuedTrack,
  PartySlug,
  SearchResultTrack,
  TakenWant,
} from '@mixwire/contracts';

const MS_PER_SECOND = 1000;

const PLAYED_THRESHOLD_SECONDS = String(LISTEN_THRESHOLD_MS_DEFAULT / MS_PER_SECOND);

/**
 * How long the console will wait for a skip to show up before it gives up and shows the deck as it
 * last read it. Two polls: one for the sample Spotify was already preparing, one for the sample
 * that can actually know about the skip.
 */
const SKIP_SETTLE_MS = PLAYBACK_POLL_INTERVAL_MS * 2;

/**
 * How long the console believes its own pause or play over what it last read. Spotify answers the
 * write with no body, so between the press and the next sample the server's idea of the deck is
 * simply out of date — and a button that re-reads that stale answer offers "pause" to a DJ who has
 * just paused, which sends a second pause rather than the play they wanted. Held for the same two
 * polls a skip is, then dropped: if the deck really did not do as it was told, the truth should win.
 */
const TRANSPORT_SETTLE_MS = PLAYBACK_POLL_INTERVAL_MS * 2;

const CONSOLE_BUTTON = pillButton('quiet', 'sm');
const CONSOLE_ACTION_BUTTON = pillButton('accent', 'sm');

/**
 * A take is only in `recentlyTaken` for TAKEN_NOTICE_WINDOW_MS, so this list empties itself as
 * the console polls — there is nothing to dismiss and no state to keep.
 */
const TakenNotices = ({
  taken,
  myChannelId,
}: {
  readonly taken: readonly TakenWant[];
  readonly myChannelId: ChannelId;
}): ReactElement | null =>
  taken.length === 0 ? null : (
    <ul className="mt-3 flex list-none flex-col gap-1.5 p-0">
      {taken.map((item) => (
        <li
          key={item.id}
          className="bg-surface-sunken rounded-control flex items-center gap-2 px-2.5 py-1.5"
        >
          <span
            className="size-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: item.channelColour }}
          />
          <span className="text-secondary truncate text-xs">
            <span className="text-content font-semibold">
              {item.channelId === myChannelId ? 'You' : item.channelLabel}
            </span>{' '}
            queued “{item.title}”
          </span>
        </li>
      ))}
    </ul>
  );

const ageLabel = (minutes: number): string =>
  minutes <= 0 ? 'just now' : `${String(minutes)} min ago`;

const WantCard = ({
  want,
  colour,
  myChannelId,
  isDismissing,
  isQueueing,
  onDismiss,
  onQueue,
}: {
  readonly want: CrowdWant;
  readonly colour: string;
  readonly myChannelId: ChannelId;
  readonly isDismissing: boolean;
  readonly isQueueing: boolean;
  readonly onDismiss: () => void;
  readonly onQueue: () => void;
}): ReactElement => (
  <AccentCard colour={colour}>
    <div className="flex items-center gap-3">
      {want.trackUrl === null ? (
        <>
          <TrackTile title={want.title} colour={colour} imageUrl={want.artUrl} />
          <div className="min-w-0 flex-1">
            <p className="text-content truncate text-sm font-semibold">{want.title}</p>
            <p className="text-secondary truncate text-xs">{want.artist}</p>
          </div>
        </>
      ) : (
        /* The sleeve and title link to Spotify in a new tab, so the DJ never navigates away from the console. */
        <a
          href={want.trackUrl}
          target="_blank"
          rel="noreferrer"
          title={`Open “${want.title}” in Spotify`}
          className="group flex min-w-0 flex-1 items-center gap-3 rounded-control"
        >
          <TrackTile title={want.title} colour={colour} imageUrl={want.artUrl} />
          <div className="min-w-0 flex-1">
            <p className="text-content group-hover:decoration-accent truncate text-sm font-semibold group-hover:underline group-hover:decoration-2 group-hover:underline-offset-2">
              {want.title}
            </p>
            <p className="text-secondary truncate text-xs">{want.artist}</p>
          </div>
        </a>
      )}
      {want.suggestedCount <= 1 ? null : (
        <span className="bg-canvas text-secondary w-14 shrink-0 rounded-pill py-1 text-center text-xs font-semibold">
          ×{want.suggestedCount}
        </span>
      )}
      <span className="text-muted w-16 shrink-0 text-right text-xs">
        {ageLabel(want.ageMinutes)}
      </span>
    </div>

    <TrackFlagNote
      item={want}
      myChannelId={myChannelId}
      clashLabel="Already queued on another channel"
    />

    <div className="mt-3 flex items-center gap-2.5">
      {/* Nothing to send Spotify for a want recorded before track ids were stored, so the
          button that would do it is not offered rather than offered and refused. */}
      {want.trackUrl === null ? null : (
        <button
          type="button"
          disabled={isQueueing || isDismissing}
          onClick={onQueue}
          className={CONSOLE_ACTION_BUTTON}
        >
          {isQueueing ? 'Adding…' : 'Add to queue'}
        </button>
      )}
      <button
        type="button"
        disabled={isDismissing || isQueueing}
        onClick={onDismiss}
        className={CONSOLE_BUTTON}
      >
        Dismiss
      </button>
    </div>
  </AccentCard>
);

const QueueCard = ({
  track,
  colour,
  myChannelId,
  nowMs,
}: {
  readonly track: DjQueuedTrack;
  readonly colour: string;
  readonly myChannelId: ChannelId;
  readonly nowMs: number;
}): ReactElement => (
  <AccentCard colour={colour} className="p-3">
    <div className="flex items-center gap-3">
      <TrackRow
        title={track.title}
        artist={track.artist}
        colour={colour}
        size={38}
        imageUrl={track.imageUrl}
      />
      <span className="text-muted shrink-0 text-xs">{formatStartsIn(track.startsAt, nowMs)}</span>
    </div>
    <TrackFlagNote
      item={track}
      myChannelId={myChannelId}
      clashLabel="Another channel plays this before you"
    />
  </AccentCard>
);

const livePercent = (nowPlaying: ChannelNowPlaying, elapsedMs: number): number =>
  Math.min(
    100,
    ((nowPlaying.positionSeconds + elapsedMs / MS_PER_SECOND) / nowPlaying.durationSeconds) * 100,
  );

const OtherChannelCard = ({
  other,
  nowMs,
}: {
  readonly other: OtherChannel;
  readonly nowMs: number;
}): ReactElement => {
  // Read from the party cache on the view's refetch clock, filled in between
  // polls the same way this DJ's own progress bar is.
  const elapsedMs = useLiveProgress(other.nowPlaying, other.nowPlaying?.playing === true);
  const alreadyPlayed = withoutNowPlaying(other.played, other.nowPlaying);

  return (
    <AccentCard colour={other.channel.colour}>
      <div className="flex items-baseline justify-between gap-2">
        <span
          className="text-xs font-medium tracking-widest uppercase"
          style={{ color: other.channel.colour }}
        >
          {colourName(other.channel.colour)}
        </span>
        <span className="text-muted truncate text-xs">{other.channel.displayName}</span>
      </div>

      <RateLimitLine rateLimit={other.rateLimit} />

      {other.nowPlaying === null ? (
        <p className="text-muted mt-2.5 text-xs">Not connected</p>
      ) : (
        <div className="mt-2.5">
          <p className="text-muted text-[10px] tracking-widest uppercase">Playing</p>
          <div className="mt-1.5 flex items-center gap-2.5">
            <TrackTile
              title={other.nowPlaying.title}
              colour={other.channel.colour}
              size={36}
              imageUrl={other.nowPlaying.imageUrl}
            />
            <div className="min-w-0 flex-1">
              <p className="text-content truncate text-sm font-medium">{other.nowPlaying.title}</p>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="bg-line h-[3px] flex-1 overflow-hidden rounded-full">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${String(livePercent(other.nowPlaying, elapsedMs))}%`,
                      backgroundColor: other.channel.colour,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {other.next.length === 0 ? null : (
        <div className="mt-3">
          <p className="text-muted text-[10px] tracking-widest uppercase">Coming</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {other.next.map((track) => (
              <li key={track.id} className="text-secondary flex justify-between gap-2 text-xs">
                <span className="min-w-0 truncate">{track.title}</span>
                <span className="text-muted shrink-0">{formatStartsIn(track.startsAt, nowMs)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {alreadyPlayed.length === 0 ? null : (
        <div className="mt-3">
          <p className="text-muted text-[10px] tracking-widest uppercase">Already played</p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {alreadyPlayed.map((track) => (
              <li key={track.id} className="text-muted truncate text-xs tabular-nums">
                {track.playedAt} {track.title}
              </li>
            ))}
          </ul>
        </div>
      )}
    </AccentCard>
  );
};

const ChannelDots = ({
  channels,
  activeChannelId,
}: {
  readonly channels: readonly Channel[];
  readonly activeChannelId: ChannelId;
}): ReactElement => (
  <div className="flex items-center gap-1.5" title="Channels connected at this party">
    {channels.map((channel) => (
      <span
        key={channel.id}
        className="size-3 rounded-full"
        style={{
          backgroundColor: channel.connected ? channel.colour : 'var(--ui-border)',
          outline: channel.id === activeChannelId ? `2px solid ${channel.colour}` : undefined,
          outlineOffset: 2,
        }}
      />
    ))}
  </div>
);

type AirState = 'on-air' | 'paused' | 'silent';

/**
 * `isPlaying: false` from this DJ's own stream carries no track, so it cannot
 * tell a pause apart from silence on its own — the cached track decides which.
 */
const airStateOf = (
  livePlaying: NowPlaying | undefined,
  cachedNowPlaying: ChannelNowPlaying | null | undefined,
): AirState => {
  if (livePlaying?.isPlaying === true) {
    return 'on-air';
  }
  if (cachedNowPlaying === null || cachedNowPlaying === undefined) {
    return 'silent';
  }
  if (livePlaying !== undefined) {
    return 'paused';
  }
  return cachedNowPlaying.playing ? 'on-air' : 'paused';
};

const AIR_STATE_STYLES: Readonly<
  Record<AirState, { readonly label: string; readonly pill: string; readonly dot: string }>
> = {
  'on-air': { label: 'On air', pill: 'bg-success-subtle text-content', dot: 'bg-success' },
  paused: { label: 'Paused', pill: 'bg-warning-subtle text-content', dot: 'bg-warning' },
  silent: { label: 'Nothing playing', pill: 'border-subtle text-muted border', dot: 'bg-line' },
};

const AirStatePill = ({ state }: { readonly state: AirState }): ReactElement => {
  const style = AIR_STATE_STYLES[state];

  return (
    <span
      className={`flex items-center gap-2 rounded-pill px-3 py-1 text-xs font-semibold tracking-widest uppercase ${style.pill}`}
    >
      <span className={`size-2 shrink-0 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
};

const DjHeader = ({
  slug,
  channel,
  channels,
  partyDisplayName,
  nowPlayingTitle,
  airState,
  nowMs,
  isBusy,
  user,
  onShare,
  onLeave,
}: {
  readonly slug: PartySlug;
  readonly channel: Channel;
  readonly channels: readonly Channel[];
  readonly partyDisplayName: string;
  readonly nowPlayingTitle: string | undefined;
  readonly airState: AirState;
  readonly nowMs: number;
  readonly isBusy: boolean;
  readonly user: SessionUser | undefined;
  readonly onShare: () => void;
  readonly onLeave: () => void;
}): ReactElement => {
  const { navigate } = useRoute();

  return (
    <>
      <div className="bg-surface border-subtle flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b px-6 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              navigate(LOBBY_PATH);
            }}
            className={CONSOLE_BUTTON}
          >
            All parties
          </button>
          <button
            type="button"
            onClick={() => {
              navigate(partyPath(slug));
            }}
            className={CONSOLE_BUTTON}
          >
            Public view
          </button>
          <button type="button" onClick={onShare} className={pillButton('accent', 'sm')}>
            Share
          </button>
          <span className="bg-line mx-1 h-4 w-px" />
          <button type="button" disabled={isBusy} onClick={onLeave} className={CONSOLE_BUTTON}>
            Leave
          </button>
        </div>

        {user === undefined ? null : (
          <a
            href={partyPath(slug)}
            onClick={(event) => {
              event.preventDefault();
              navigate(partyPath(slug));
            }}
            className="hover:bg-surface-hover -my-1 flex min-w-0 items-center gap-2 rounded-pill py-1 pr-3 pl-1 transition-colors"
          >
            <Avatar displayName={user.displayName} avatarUrl={user.avatarUrl} />
            <span className="text-content truncate text-sm font-medium">{user.displayName}</span>
          </a>
        )}
      </div>

      <div
        className="flex flex-wrap items-center gap-x-5 gap-y-2 px-6 py-4"
        style={{ backgroundColor: channel.colour }}
      >
        <span className="text-canvas flex shrink-0 items-center gap-2">
          <Logo className="size-6" />
          <span className="font-display text-lg">MixWire</span>
        </span>
        <span className="bg-canvas/30 h-5 w-px shrink-0" />
        <span className="text-canvas min-w-0 flex-1 truncate text-sm font-medium">
          {partyDisplayName}
        </span>
        <span className="text-canvas flex shrink-0 items-center gap-2.5">
          <span
            title="Your time on set at this party, banked stints included"
            className="text-sm font-medium tabular-nums"
          >
            On deck {formatTimeOnSet(channel, nowMs)}
          </span>
          <SyncMeter onColour={true} />
        </span>
      </div>

      <div className="bg-surface border-subtle flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-6 py-4">
        <div className="min-w-0">
          <p className="text-muted text-xs tracking-widest uppercase">
            Your channel · {colourName(channel.colour)}
          </p>
          <h2 className="font-display text-content mt-0.5 truncate text-2xl">
            {channel.displayName}
          </h2>
        </div>
        <AirStatePill state={airState} />
        <ChannelDots channels={channels} activeChannelId={channel.id} />
        {nowPlayingTitle === undefined ? null : (
          <span className="text-secondary min-w-0 truncate text-sm">{nowPlayingTitle}</span>
        )}
      </div>
    </>
  );
};

type DjPageProps = {
  readonly slug: PartySlug;
  readonly channelId: ChannelId;
};

// The DJ's own progress bar uses a personal per-second Spotify poll;
// everything else on the page comes from the shared server cache all consoles see.
const deriveLiveNowPlaying = (
  livePlaying: NowPlaying | undefined,
  liveProgressMs: number,
): ChannelNowPlaying | undefined => {
  if (livePlaying?.isPlaying !== true) {
    return undefined;
  }

  return {
    title: livePlaying.track.name,
    artist: livePlaying.track.artists.join(', '),
    art: livePlaying.track.albumName,
    imageUrl: livePlaying.track.albumImageUrl,
    positionSeconds: Math.floor((livePlaying.progressMs + liveProgressMs) / 1000),
    durationSeconds: Math.floor(livePlaying.track.durationMs / 1000),
    playing: true,
  };
};

type ConsoleModal = 'none' | 'share' | 'leave' | 'end-party';

const ConsoleModals = ({
  openModal,
  slug,
  partyDisplayName,
  passkey,
  isEndPending,
  isLeavePending,
  onClose,
  onLeave,
  onAskEndParty,
  onConfirmEndParty,
}: {
  readonly openModal: ConsoleModal;
  readonly slug: PartySlug;
  readonly partyDisplayName: string;
  readonly passkey: string;
  readonly isEndPending: boolean;
  readonly isLeavePending: boolean;
  readonly onClose: () => void;
  readonly onLeave: () => void;
  readonly onAskEndParty: () => void;
  readonly onConfirmEndParty: () => void;
}): ReactElement | null => {
  switch (openModal) {
    case 'share': {
      return (
        <ShareModal
          slug={slug}
          partyDisplayName={partyDisplayName}
          passkey={passkey}
          onClose={onClose}
        />
      );
    }
    case 'leave': {
      return (
        <LeaveModal
          partyDisplayName={partyDisplayName}
          isPending={isLeavePending}
          onLeave={onLeave}
          onEndParty={onAskEndParty}
          onCancel={onClose}
        />
      );
    }
    case 'end-party': {
      return (
        <EndPartyConfirmModal
          partyDisplayName={partyDisplayName}
          isPending={isEndPending}
          onConfirm={onConfirmEndParty}
          onCancel={onClose}
        />
      );
    }
    case 'none': {
      return null;
    }
  }
};

/**
 * The console is a signed-in surface: it mirrors a real Spotify account, and
 * nobody who is not signed in has one to mirror. Signing in from here lands on
 * the lobby opened at this party, because being signed in is not the same as
 * holding one of its channels.
 */
const SignInToDjGate = ({ slug }: { readonly slug: PartySlug }): ReactElement => (
  <div className="bg-surface rounded-card border-subtle mx-auto flex max-w-sm flex-col items-center gap-4 border p-8 text-center">
    <Logo className="text-content size-12" />
    <p className="text-muted text-xs tracking-widest uppercase">DJ console</p>
    <h1 className="font-display text-content text-2xl">Sign in to take a channel</h1>
    <p className="text-secondary text-sm">
      MixWire follows your own Spotify playback, so a console needs an account behind it.
    </p>
    <a href={loginReturningTo(lobbyJoinPath(slug))} className={`${pillButton('accent')} mt-2`}>
      Log in with Spotify
    </a>
    <a href={partyPath(slug)} className="text-secondary text-sm underline underline-offset-2">
      Just watch the party instead
    </a>
  </div>
);

/**
 * Skipping is the only way to take a track off a Spotify queue: the Web API can add to one and
 * advance it, but offers nothing that removes an entry further down. So this sits on the track
 * that is playing, not on the queue list, where a remove button would have to be a lie.
 */
/**
 * What the deck looks like between a skip landing at Spotify and the next sample coming back.
 * Spotify acknowledges the write with no body, so for a second or two the console genuinely does
 * not know what is playing — and showing the track that was just skipped would be a worse answer
 * than admitting that.
 */
const OnAirSkeleton = (): ReactElement => (
  <div
    aria-busy="true"
    aria-label="Waiting for the next track"
    className="flex animate-pulse gap-3"
  >
    <div className="bg-surface-sunken rounded-control size-[72px] shrink-0" />
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
      <div className="bg-surface-sunken h-3.5 w-3/4 rounded-pill" />
      <div className="bg-surface-sunken h-3 w-1/2 rounded-pill" />
      <div className="bg-surface-sunken mt-1 h-1.5 w-full rounded-pill" />
    </div>
  </div>
);

/**
 * A channel that reports no track is not the same as a channel with no deck behind it: Spotify
 * says "not playing" without naming what is loaded, so the console genuinely cannot tell whether
 * the DJ paused mid-track or has yet to start one. It says that instead of guessing.
 */
const OnAirNothingReported = (): ReactElement => (
  <p className="text-secondary text-sm">
    Spotify isn&rsquo;t reporting a track on your deck. Press play to pick up where you left off.
  </p>
);

const OnAirBody = ({
  nowPlaying,
  colour,
  isOnAir,
  isAwaitingSkip,
}: {
  readonly nowPlaying: ChannelNowPlaying | null;
  readonly colour: string;
  readonly isOnAir: boolean;
  readonly isAwaitingSkip: boolean;
}): ReactElement => {
  if (isAwaitingSkip) {
    return <OnAirSkeleton />;
  }
  if (nowPlaying === null) {
    return <OnAirNothingReported />;
  }
  return (
    <NowPlayingBlock
      title={nowPlaying.title}
      artist={nowPlaying.artist}
      colour={colour}
      size={72}
      imageUrl={nowPlaying.imageUrl}
      positionSeconds={nowPlaying.positionSeconds}
      durationSeconds={nowPlaying.durationSeconds}
      playing={isOnAir}
    />
  );
};

/**
 * The transport outlives the track: a paused deck reports as not playing at all, so a card that
 * hid itself when there was nothing to show took the play button away from the one DJ who needed
 * it — the one who had just pressed pause.
 */
const OnAirCard = ({
  nowPlaying,
  colour,
  isOnAir,
  isBusy,
  isAwaitingSkip,
  onSkip,
  onTogglePlay,
}: {
  readonly nowPlaying: ChannelNowPlaying | null;
  readonly colour: string;
  readonly isOnAir: boolean;
  readonly isBusy: boolean;
  readonly isAwaitingSkip: boolean;
  readonly onSkip: () => void;
  readonly onTogglePlay: () => void;
}): ReactElement => (
  <div className="bg-surface rounded-card border-subtle mt-3 border p-4">
    <OnAirBody
      nowPlaying={nowPlaying}
      colour={colour}
      isOnAir={isOnAir}
      isAwaitingSkip={isAwaitingSkip}
    />

    <div className="mt-3 flex items-center gap-2">
      <TransportButton
        icon={isOnAir ? 'pause' : 'play'}
        label={isOnAir ? 'Pause your deck' : 'Resume your deck'}
        isDisabled={isBusy}
        onPress={onTogglePlay}
      />
      <TransportButton icon="skip" label="Skip this track" isDisabled={isBusy} onPress={onSkip} />
    </div>
  </div>
);

/**
 * A write Spotify refused for want of Premium is the one failure a line of red text understates:
 * it is not this track, this moment or this deck, it is the account, and no amount of pressing
 * will change it. So it gets the same full answer the standing banner gives.
 */
const WriteFailureNotice = ({
  failure,
}: {
  readonly failure: ApiFailure | undefined;
}): ReactElement | null => {
  if (failure === undefined) {
    return null;
  }
  return failure.code === 'payment_required' ? (
    <PremiumRequiredWriteNotice />
  ) : (
    <ErrorBanner>{failure.message}</ErrorBanner>
  );
};

// Matches the suggester's own debounce: the input stays instant and only the searching waits.
const SEARCH_DEBOUNCE_MS = 300;

/**
 * How long a search result keeps its "Added ✓" receipt. Long enough that the DJ who pressed the
 * button sees it answered, short enough that one who then took the track back off their Spotify
 * queue is not locked out of sending it again by a row that never stops congratulating itself.
 */
const ADDED_CONFIRMATION_MS = 30_000;

const NO_WANTS_YET = (
  <p className="text-muted py-10 text-center text-sm">
    Nothing waiting. Suggestions land here, most-wanted first.
  </p>
);

/**
 * The console's one place for deciding what plays next: what the room has asked for, and a search
 * of all of Spotify for what it hasn't. The search is the surface and the wants are what shows
 * through it when nothing is being searched — including after `SEARCH_IDLE_RESET_MS` of a DJ not
 * touching it, since a console left on a half-typed search is showing the room's list to nobody.
 */
const DjSuggestionsPanel = ({
  slug,
  channelId,
  colour,
  wants,
  recentlyTaken,
  nowMs,
}: {
  readonly slug: PartySlug;
  readonly channelId: ChannelId;
  readonly colour: string;
  readonly wants: readonly CrowdWant[];
  readonly recentlyTaken: readonly TakenWant[];
  readonly nowMs: number;
}): ReactElement => {
  const [query, setQuery] = useState('');
  // Tracks this console has just sent to Spotify, and when. The row itself is the receipt: a search
  // result that was added looks no different afterwards otherwise, since the deck only changes a
  // poll later. Read against `nowMs` rather than cleared, exactly as the transport markers are.
  const [justAdded, setJustAdded] = useState<ReadonlyMap<string, number>>(new Map());
  const term = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const results = useDjSearch(slug, channelId, term);
  const dismiss = useDismissWant();
  const queueWant = useQueueWant();
  const queueTrack = useQueueTrack();

  const noteActivity = useIdleReset({
    isArmed: query !== '',
    resetAfterMs: SEARCH_IDLE_RESET_MS,
    onReset: () => {
      setQuery('');
    },
  });

  // A refused write is answered above the panel rather than inside the results list, which the
  // idle reset can clear out from under it.
  const writeFailure = readApiFailure(queueTrack.error) ?? readApiFailure(queueWant.error);
  const searchFailure = readApiFailure(results.error);
  // True only while there is nothing to show for this term; a background refetch leaves the
  // current results in place.
  const isSearching = term !== '' && results.data === undefined && results.isFetching;

  const isQueueingWant = (want: CrowdWant): boolean =>
    queueWant.isPending &&
    queueWant.variables.slug === slug &&
    queueWant.variables.channelId === channelId &&
    queueWant.variables.wantId === want.id;

  const wasJustAdded = (track: SearchResultTrack): boolean => {
    const addedAtMs = justAdded.get(track.id);
    return addedAtMs !== undefined && nowMs - addedAtMs < ADDED_CONFIRMATION_MS;
  };

  const handleQueueTrack = (track: SearchResultTrack): void => {
    noteActivity();
    queueTrack.mutate(
      { slug, channelId, track: track.id },
      {
        onSuccess: () => {
          setJustAdded((added) => new Map<string, number>([...added, [track.id, nowMs]]));
        },
      },
    );
  };

  return (
    <div className="border-subtle flex min-w-0 flex-1 flex-col border-b lg:border-r lg:border-b-0">
      <div className="px-4 pt-6 pb-4 sm:px-6">
        <p className="text-muted text-xs tracking-widest uppercase">
          Crowd wants &amp; search · {wants.length}
        </p>
        <p className="text-muted mt-1 max-w-prose text-xs">
          One shared list, most-wanted first. Nobody picks a channel — you decide if it fits. Search
          Spotify below for anything the room hasn&rsquo;t asked for.
        </p>

        <WriteFailureNotice failure={writeFailure} />

        <TakenNotices taken={recentlyTaken} myChannelId={channelId} />
      </div>

      <TrackSearch
        query={query}
        onQueryChange={setQuery}
        term={term}
        results={results.data?.items ?? []}
        myChannelId={channelId}
        isSearching={isSearching}
        failureMessage={searchFailure?.message}
        inputLabel="Search Spotify to queue a track"
        actionLabel={(track) => {
          if (wasJustAdded(track)) {
            return 'Added ✓';
          }
          return queueTrack.isPending && queueTrack.variables.track === track.id
            ? 'Adding…'
            : 'Add to queue';
        }}
        isActionDisabled={(track) =>
          wasJustAdded(track) || (queueTrack.isPending && queueTrack.variables.track === track.id)
        }
        onAction={handleQueueTrack}
        onActivity={noteActivity}
        listClassName="px-4 pb-6 sm:px-6"
        emptyQueryContent={
          wants.length === 0 ? (
            NO_WANTS_YET
          ) : (
            <ul className="flex list-none flex-col gap-2.5 p-0 pt-1">
              {wants.map((want) => (
                <WantCard
                  key={want.id}
                  want={want}
                  colour={colour}
                  myChannelId={channelId}
                  isDismissing={
                    dismiss.isPending &&
                    dismiss.variables.slug === slug &&
                    dismiss.variables.channelId === channelId &&
                    dismiss.variables.wantId === want.id
                  }
                  isQueueing={isQueueingWant(want)}
                  onDismiss={() => {
                    dismiss.mutate({ slug, channelId, wantId: want.id });
                  }}
                  onQueue={() => {
                    queueWant.mutate({ slug, channelId, wantId: want.id });
                  }}
                />
              ))}
            </ul>
          )
        }
      />
    </div>
  );
};

export const DjPage = ({ slug, channelId }: DjPageProps): ReactElement | null => {
  const [openModal, setOpenModal] = useState<ConsoleModal>('none');
  const djView = useDjView(slug, channelId);
  const skip = useSkipCurrent();
  const transport = useTransport();
  // What the deck was on when skip was pressed, and when. Both halves are needed: the skeleton
  // should clear the moment the deck is genuinely on something else, but it must also clear on its
  // own if that never happens, or a refused skip would leave the console waiting all night.
  const [skipped, setSkipped] = useState<{ readonly title: string; readonly atMs: number }>();
  // What this console last told the deck to do, and when. See TRANSPORT_SETTLE_MS.
  const [intent, setIntent] = useState<{ readonly playing: boolean; readonly atMs: number }>();
  const authStatus = useAuthStatus();
  const livePlaying = useNowPlaying();
  const liveProgressMs = useLiveProgress(livePlaying, livePlaying?.isPlaying === true);
  const leave = useLeaveChannel();
  const end = useEndParty();
  const { navigate } = useRoute();
  // One clock for the whole page, rather than each counting-down list item running its own interval.
  const nowMs = useNow(LIVE_TICK_MS);

  usePageTitle(djView.data?.partyDisplayName);

  if (authStatus.data === undefined) {
    return null;
  }

  if (!authStatus.data.authenticated) {
    return <SignInToDjGate slug={slug} />;
  }

  const isEnded = (djView.data?.endedAt ?? null) !== null;

  if (isEnded && djView.data !== undefined) {
    return (
      <PartyEndedModal
        partyDisplayName={djView.data.partyDisplayName}
        endedBy={djView.data.endedBy}
        onAcknowledge={() => {
          navigate(LOBBY_PATH);
        }}
      />
    );
  }

  const sessionUser = authStatus.data.user;

  const myRateLimit = djView.data?.myRateLimit ?? null;

  const liveNowPlaying = deriveLiveNowPlaying(livePlaying, liveProgressMs);
  const effectiveNowPlaying = liveNowPlaying ?? djView.data?.myNowPlaying;
  // Served from the party cache, so it's the same list every console shows
  // for this channel, not one assembled locally per tab.
  const playedRows = withoutNowPlaying(djView.data?.myPlayed ?? [], effectiveNowPlaying);
  const airState = airStateOf(livePlaying, djView.data?.myNowPlaying);

  /**
   * Read rather than cleared, exactly as the skip marker is: `nowMs` only grows, so a stale intent
   * can never speak for a deck it stopped describing, and once the sample catches up both halves
   * agree anyway and the fallback is invisible.
   */
  const isPlaying =
    intent === undefined || transport.isError || nowMs - intent.atMs >= TRANSPORT_SETTLE_MS
      ? airState === 'on-air'
      : intent.playing;

  // Spotify refuses a write with no active device or without Premium, and either is the DJ's
  // to fix, so it is said out loud rather than left as a button that quietly did nothing.
  const transportFailure = readApiFailure(skip.error) ?? readApiFailure(transport.error);

  // Read rather than cleared, so the marker going stale costs nothing: `nowMs` only ever grows, so
  // an old one can never put the console back into a wait it has already come out of — which a
  // plain flag would, the next time the room hears the same track.
  const isAwaitingSkip =
    skip.isPending ||
    (skipped !== undefined &&
      !skip.isError &&
      effectiveNowPlaying?.title === skipped.title &&
      nowMs - skipped.atMs < SKIP_SETTLE_MS);

  const closeModal = (): void => {
    setOpenModal('none');
  };

  // Navigating only on success keeps the DJ on the console if the slot or
  // party could not actually be released.
  const handleLeave = (): void => {
    leave.mutate(
      { slug, channelId },
      {
        onSuccess: () => {
          navigate(LOBBY_PATH);
        },
      },
    );
  };

  const handleConfirmEndParty = (): void => {
    end.mutate(slug, {
      onSuccess: () => {
        navigate(partyPath(slug));
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {djView.isPending ? <LoadingStatus>Loading the channel…</LoadingStatus> : null}

      {djView.isError ? <ErrorBanner>Could not load this channel.</ErrorBanner> : null}

      <WriteFailureNotice failure={transportFailure} />

      <RateLimitBanner rateLimit={myRateLimit} />

      <PremiumRequiredBanner />

      {djView.data === undefined ? null : (
        <>
          <ConsoleModals
            openModal={openModal}
            slug={slug}
            partyDisplayName={djView.data.partyDisplayName}
            passkey={djView.data.passkey}
            isEndPending={end.isPending}
            isLeavePending={leave.isPending}
            onClose={closeModal}
            onLeave={handleLeave}
            onAskEndParty={() => {
              setOpenModal('end-party');
            }}
            onConfirmEndParty={handleConfirmEndParty}
          />
          <div className="bg-surface rounded-card border-subtle overflow-hidden border">
            <DjHeader
              slug={slug}
              channel={djView.data.myChannel}
              channels={[
                djView.data.myChannel,
                ...djView.data.otherChannels.map((other) => other.channel),
              ]}
              partyDisplayName={djView.data.partyDisplayName}
              nowPlayingTitle={effectiveNowPlaying?.title}
              airState={airState}
              nowMs={nowMs}
              isBusy={leave.isPending || end.isPending}
              user={sessionUser}
              onShare={() => {
                setOpenModal('share');
              }}
              onLeave={() => {
                setOpenModal('leave');
              }}
            />

            <div className="flex flex-col lg:flex-row">
              <DjSuggestionsPanel
                slug={slug}
                channelId={channelId}
                colour={djView.data.myChannel.colour}
                wants={djView.data.wants}
                recentlyTaken={djView.data.recentlyTaken}
                nowMs={nowMs}
              />

              <div className="border-subtle min-w-0 flex-1 border-b p-6 lg:border-r lg:border-b-0">
                <p className="text-muted text-xs tracking-widest uppercase">
                  Your queue · {djView.data.myQueue.length}
                </p>
                <p className="text-muted mt-1 max-w-prose text-xs">
                  Read from Spotify, in play order. Reorder or drop tracks in Spotify — MixWire only
                  mirrors it.
                </p>

                {djView.data.myQueue.length === 0 ? (
                  <p className="border-subtle text-muted rounded-card mt-4 border border-dashed p-6 text-center text-sm">
                    Your queue is empty.
                  </p>
                ) : (
                  <ul className="mt-4 flex list-none flex-col gap-2.5 p-0">
                    {djView.data.myQueue.map((track) => (
                      <QueueCard
                        key={track.id}
                        track={track}
                        colour={djView.data.myChannel.colour}
                        myChannelId={djView.data.myChannel.id}
                        nowMs={nowMs}
                      />
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-surface-sunken w-full shrink-0 p-6 lg:w-80">
                <p className="text-muted text-xs tracking-widest uppercase">On air now</p>

                <OnAirCard
                  nowPlaying={effectiveNowPlaying ?? null}
                  colour={djView.data.myChannel.colour}
                  isOnAir={isPlaying}
                  isBusy={skip.isPending || transport.isPending}
                  isAwaitingSkip={isAwaitingSkip}
                  onSkip={() => {
                    const title = effectiveNowPlaying?.title;
                    setSkipped(title === undefined ? undefined : { title, atMs: nowMs });
                    skip.mutate({ slug, channelId });
                  }}
                  onTogglePlay={() => {
                    // Chosen from what the console is showing, not from the last sample — those
                    // differ for exactly as long as this matters.
                    setIntent({ playing: !isPlaying, atMs: nowMs });
                    transport.mutate({
                      slug,
                      channelId,
                      action: isPlaying ? 'pause' : 'resume',
                    });
                  }}
                />

                {djView.data.otherChannels.length === 0 ? null : (
                  <>
                    <p className="text-muted mt-5 text-xs tracking-widest uppercase">
                      The other channels
                    </p>
                    <ul className="mt-3 flex list-none flex-col gap-2.5 p-0">
                      {djView.data.otherChannels.map((other) => (
                        <OtherChannelCard key={other.channel.id} other={other} nowMs={nowMs} />
                      ))}
                    </ul>
                  </>
                )}

                <p className="text-muted mt-5 text-xs tracking-widest uppercase">
                  Played · {playedRows.length}{' '}
                  <InfoTooltip label="What counts as played?">
                    A track lands here once it has been playing for {PLAYED_THRESHOLD_SECONDS}{' '}
                    seconds — anything shorter was a skip, not a play. If that is the wrong line to
                    draw for your room, tell the MixWire crew.
                  </InfoTooltip>
                </p>
                <ul className="mt-2 flex list-none flex-col gap-1 p-0">
                  {playedRows.map((track) => (
                    <li key={track.id} className="flex items-center gap-2.5 py-1">
                      <TrackRow
                        title={track.title}
                        artist={track.artist}
                        colour={djView.data.myChannel.colour}
                        size={28}
                        imageUrl={track.imageUrl}
                        titleClassName="text-content truncate text-sm"
                      />
                      <span className="text-muted shrink-0 text-xs tabular-nums">
                        {track.playedAt}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
